import abstractPlayer from "./player/abstract";
import accountToggle from "./account-toggle";
import adjustExternalLinks from "./adjust-external-links";
import ajaxFlash from "./ajax-flash";
import animateBackground from "./animate-background";
import calendar from "./calendar";
import characterCountdown from "./character-countdown";
import confirmAction from "./confirm-action";
import copyToClipboard from "./copy-to-clipboard";
import creditCardForm from "./credit-card-form";
import csrfTag from "./csrf";
import donationRadio from "./donation-radio";
import editContent from "./edit-content";
import eventFilters from "./event-filters";
import fetchAd from "./views/fetch-ad";
import flash from "./flash";
import historyProgress from "./history-progress";
import inViewport from "./in-viewport";
import mailchimpForm from "./mailchimp-form";
import mastheadPlayer from "./masthead-player";
import onAirToggle from "./onair-toggle";
import pausePlayerOnClick from "./pause-player-on-click";
import playable from "./playable";
import player from "./player";
import responsiveEmbed from "./responsive-embed";
import revealMobileNav from "./reveal-mobile-nav";
import scheduleCalendar from "./schedule-calendar";
import searchFilters from "./search-filters";
import specialMode from "./special-mode";
import subscriberGiveawayEnterButton from "./subscriber-giveaway-enter-button";
import subscriptionCard from "./subscription-card/subscription-card";
import subscriptionProcessingCheck from "./subscription-processing-check";
import timeslotList from "./timeslot-list";
import toggleCardForm from "./toggle-card-form";
import toggleClass from "./toggle-class";
import toggleGenderSelfDescribed from "./toggle-gender-self-described";
import toggleInternationalAddress from "./toggle-international-address";
import triggerLiveStream from "./trigger-live-stream";
import waitForCsrf from "./wait-for-csrf";

/**
 * Defer importing a viewloader view until it's called
 *
 * There are some limitations in webpack around dynamic expressions, and so we
 * need to have the view base be a known path like `../`
 *
 * https://webpack.js.org/guides/migrating/#dynamic-expressions
 *
 * @param {String} path Path to view module (relative to entry root)
 */
type View = void | { destroy(): void };
type ViewFunc = (el: HTMLElement, props: any) => (View | Promise<View>);
type ViewModule = { default: ViewFunc };
function deferredView(dynamicImport: () => Promise<ViewModule>): ViewFunc {
  return (el, props) => {
    return dynamicImport().then(mod => mod.default(el, props));
  };
}

type ViewMap = { [viewName: string]: ViewFunc };

/**
 * Viewloader-compatible view functions
 * https://github.com/icelab/viewloader
 */
export default {
  // must be initialized first as it clones element and replaces in DOM for some reason?
  toggleClass,

  // init the rest in whatever order:
  abstractPlayer,
  flash,
  csrfTag,
  waitForCsrf,
  fetchAd,
  adjustExternalLinks,
  donationRadio,
  subscriptionCard,
  animateBackground,
  calendar,
  characterCountdown,
  colorFromImage: deferredView(() => import("./color-from-image")),
  subscriberGiveawayEnterButton,
  copyToClipboard,
  editContent,
  eventFilters,
  searchFilters,
  inViewport,
  mapEmbed: deferredView(() => import("./map-embed")),
  player,
  scheduleCalendar,
  accountToggle,
  ajaxFlash,
  confirmAction,
  creditCardForm,
  historyProgress,
  playable,
  mastheadPlayer,
  mailchimpForm,
  onAirToggle,
  pausePlayerOnClick,
  responsiveEmbed,
  revealMobileNav,
  subscriptionProcessingCheck,
  timeslotList,
  toggleCardForm,
  toggleInternationalAddress,
  triggerLiveStream,
  specialMode,
  toggleGenderSelfDescribed,
} satisfies ViewMap;
